import axios from 'axios';

// Normalize outcome_id for Point Spread to include signed points and remove ":vs:Opponent"
function normalizeOutcomeId(outcomeId, options = {}) {
    try {
        const { market, points, home_team } = options;
        if (
            market === 'Point Spread' &&
            outcomeId &&
            outcomeId.includes(':Point Spread:') &&
            outcomeId.includes(':vs:') &&
            typeof points !== 'undefined' &&
            points !== null &&
            home_team
        ) {
            const split = outcomeId.split(':');
            // Expected format: [gameId, 'Point Spread', team, 'vs', opponent]
            if (split.length >= 5) {
                const gameId = split[0];
                const marketStr = split[1];
                const team = split[2];
                const sign = team === home_team ? '-' : '+';
                const signedPoints = `${sign}${points}`;
                return `${gameId}:${marketStr}:${team}:${signedPoints}`;
            }
        }
    } catch (e) {
        // Fallback to original on any parsing errors
    }
    return outcomeId;
}

const getBackendUrl = () => {
    const url = process.env.REACT_APP_BACKEND_URL;
    if (!url || url === 'undefined') {
        throw new Error(
            'REACT_APP_BACKEND_URL is not set. Add it to your .env (e.g. REACT_APP_BACKEND_URL=http://localhost:8000) and restart the dev server.'
        );
    }
    return url;
};

export const getRelatedOdds = async (outcome_id_normalized_no_book, sport, options = {}) => {
    const baseUrl = getBackendUrl();
    const accessToken = window.localStorage.getItem('access_token');

    // Handle Moneyline with ":vs:" - fetch both teams separately and merge
    if (options.market === 'Moneyline' && outcome_id_normalized_no_book && outcome_id_normalized_no_book.includes(':vs:')) {
        try {
            const split = outcome_id_normalized_no_book.split(':');
            // Format: "32700-65233-2025-11-04:Moneyline:Stanford:vs:Portland State"
            if (split.length >= 5 && split[1] === 'Moneyline' && split[3] === 'vs') {
                const gameId = split[0];
                const marketStr = split[1];
                const team1 = split[2];
                const team2 = split[4];
                
                const team1Id = `${gameId}:${marketStr}:${team1}:`;
                const team2Id = `${gameId}:${marketStr}:${team2}:`;
                
                // Fetch both teams' odds in parallel
                const [team1Res, team2Res] = await Promise.all([
                    axios.get(
                        `${baseUrl}/related_outcomes?outcome_id_normalized_no_book=${encodeURIComponent(team1Id)}&sport=${encodeURIComponent(sport)}`,
                        {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                            },
                        }
                    ).catch(() => ({ data: [] })),
                    axios.get(
                        `${baseUrl}/related_outcomes?outcome_id_normalized_no_book=${encodeURIComponent(team2Id)}&sport=${encodeURIComponent(sport)}`,
                        {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                            },
                        }
                    ).catch(() => ({ data: [] }))
                ]);
                
                const team1Odds = Array.isArray(team1Res.data) ? team1Res.data : (team1Res.data?.related_odds || []);
                const team2Odds = Array.isArray(team2Res.data) ? team2Res.data : (team2Res.data?.related_odds || []);
                
                // Merge odds by sportsbook
                const oddsMap = new Map();
                
                // Add team1 odds (bet_price_over column)
                team1Odds.forEach(odd => {
                    if (odd.sportsbook) {
                        oddsMap.set(odd.sportsbook, {
                            ...odd,
                            bet_price_over: odd.bet_price_over,
                            bet_price_under: null, // Will be filled from team2
                        });
                    }
                });
                
                // Add team2 odds (bet_price_under column)
                team2Odds.forEach(odd => {
                    if (odd.sportsbook) {
                        const existing = oddsMap.get(odd.sportsbook);
                        if (existing) {
                            existing.bet_price_under = odd.bet_price_over; // Team2's bet_price_over becomes the under column
                        } else {
                            oddsMap.set(odd.sportsbook, {
                                ...odd,
                                bet_price_over: null,
                                bet_price_under: odd.bet_price_over,
                            });
                        }
                    }
                });
                
                return Array.from(oddsMap.values());
            }
        } catch (e) {
            // Fallback to original behavior on error
            console.error('Error fetching Moneyline odds:', e);
        }
    }

    const normalizedId = normalizeOutcomeId(outcome_id_normalized_no_book, options);

    try {
        const relatedOddsRes = await axios.get(
            `${baseUrl}/related_outcomes?outcome_id_normalized_no_book=${encodeURIComponent(normalizedId)}&sport=${encodeURIComponent(sport)}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );
        return relatedOddsRes.data;
    } catch (err) {
        if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
            throw new Error(
                `Backend unreachable at ${baseUrl}. Ensure the backend is running and CORS allows requests from this origin.`
            );
        }
        throw err;
    }
};
